import type { BaseQueryFn } from "@reduxjs/toolkit/query";
import type {
  FetchArgs,
  FetchBaseQueryError,
  FetchBaseQueryMeta,
} from "@reduxjs/toolkit/query";
import { fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { showWarningMessage } from "@/features/alertSnackbarSlice";
import { getEnv } from "./env.helpers";
import logger from "./logger";
import { deleteJwtToken, getJwtToken } from "./storage.helpers";

const RETURN_URL_KEY = "nimbus_return_url";

/**
 * Saves current URL to sessionStorage for post-login redirect.
 *
 * Uses sessionStorage (not localStorage) because the entire login flow
 * happens in a single tab via window.location redirects:
 * 1. User on /energy → 401 → redirect to /login (same tab)
 * 2. User logs in → redirect back (same tab)
 * 3. Read saved URL → navigate to original page
 *
 * sessionStorage is cleared when tab closes, preventing stale URLs.
 */
export const saveReturnUrl = () => {
  try {
    const fullPath =
      window.location.pathname + window.location.search + window.location.hash;

    if (fullPath.includes("/login")) {
      return;
    }

    /**
     * Strip the React Router basename from the full browser path.
     * React Router automatically prepends its basename when navigate() is called,
     * so we store the path relative to the basename. This prevents double-prefix:
     * - Full URL: /app/scheduling/energy
     * - Stored: /scheduling/energy (without /app)
     * - On post-login: navigate("/scheduling/energy") → React Router adds /app → /app/scheduling/energy ✓
     * If we stored the full path: navigate("/app/scheduling/energy") → /app/app/scheduling/energy ✗
     */
    const basename = String(import.meta.env.VITE_ROUTER_BASE ?? "");
    const url =
      basename && fullPath.startsWith(basename)
        ? fullPath.slice(basename.length) || "/"
        : fullPath;

    sessionStorage.setItem(RETURN_URL_KEY, url);
  } catch {
    logger.warn("Failed to save return URL");
  }
};

/**
 * Retrieves and clears the stored return URL.
 * Returns null if no URL stored or if URL is invalid.
 *
 * Security: Only allows relative URLs to prevent open redirect attacks.
 */
export const getAndClearReturnUrl = (): string | null => {
  try {
    const url = sessionStorage.getItem(RETURN_URL_KEY);

    setTimeout(() => sessionStorage.removeItem(RETURN_URL_KEY), 100);

    if (!url) {
      return null;
    }

    if (url.startsWith("http://") || url.startsWith("https://")) {
      logger.warn("Ignoring absolute return URL for security");
      return null;
    }

    return url;
  } catch {
    logger.warn("Failed to retrieve return URL");
    return null;
  }
};

/**
 * Creates a base query with automatic 401 handling for expired JWT tokens.
 *
 * This wrapper intercepts all API responses and provides centralised handling
 * for authentication errors. When a 401 Unauthorized response is detected:
 *
 * 1. Saves current URL for post-login redirect
 * 2. Shows user-friendly warning message via snackbar
 * 3. Cleans up expired token from localStorage
 * 4. Redirects to login page after brief delay (allows user to see message)
 *
 * @param baseUrl - The base URL for the API (e.g., from getBaseUrl())
 * @param options - Optional configuration for the base query
 * @param options.responseHandler - Custom response handler for non-standard API responses
 * @returns A configured base query function for RTK Query
 *
 * @example
 * ```typescript
 * export const myApi = createApi({
 *   reducerPath: "myApi",
 *   baseQuery: createBaseQueryWithAuth(getBaseUrl("/my-service/")),
 *   endpoints: (build) => ({ ... }),
 * });
 * ```
 *
 * @example With custom response handler
 * ```typescript
 * export const myApi = createApi({
 *   reducerPath: "myApi",
 *   baseQuery: createBaseQueryWithAuth(
 *     getBaseUrl("/my-service/"),
 *     {
 *       responseHandler: async (response) => {
 *         const contentType = response.headers.get("content-type");
 *         if (contentType?.includes("application/json")) {
 *           return response.json();
 *         }
 *         return response.text();
 *       }
 *     }
 *   ),
 *   endpoints: (build) => ({ ... }),
 * });
 * ```
 */
export const createBaseQueryWithAuth = (
  baseUrl: string,
  options?: {
    responseHandler?: (response: Response) => Promise<unknown>;
  }
): BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError,
  object,
  FetchBaseQueryMeta
> => {
  const baseQuery = fetchBaseQuery({
    baseUrl,
    prepareHeaders: (headers) => {
      const token = getJwtToken();
      if (token) {
        headers.set("authorization", `Bearer ${token}`);
      }
      return headers;
    },
    ...(options?.responseHandler && {
      responseHandler: options.responseHandler,
    }),
  });

  return async (args, api, extraOptions) => {
    const result = await baseQuery(args, api, extraOptions);

    const status =
      result.error?.status === "PARSING_ERROR"
        ? result.error.originalStatus
        : result.error?.status;

    if (status === 401) {
      saveReturnUrl();

      deleteJwtToken();

      if (getEnv() === "local") {
        api.dispatch(
          showWarningMessage(
            "Your session has expired. Refresh the page and set a new token."
          )
        );
      } else {
        api.dispatch(
          showWarningMessage(
            "Your session has expired. Redirecting to login..."
          )
        );

        setTimeout(() => {
          window.location.href = `${window.location.origin}/login?redirect=true`;
        }, 1500);
      }
    }

    return result;
  };
};
